/**
 * Enum for status inspection for the Company
 * @enum {string}
 * @property {string} NOT_STARTED - Created inspection but not sent
 * @property {string} SENT - Sent notification
 * @property {string} INITIATED - Client received the settings but did not proceed to the first step.
 * @property {string} IN_PROGRESS - Client started the inspection and passed at least one step
 * @property {string} IN_REVIEW - Client finished all steps and waiting for review inspector
 * @property {string} RE_DO - Inspector requested to redo the inspection
 * @property {string} EXPIRED - Inspection expired
 * @property {string} COMPLETED - Inspection completed successfully
 * @property {string} CANCELED - Inspection canceled by the company
 * @property {string} REQUIRES_ATTENTION - Inspection requires attention
 * @property {string} ON_HOLD - Inspection is on hold
 * @property {string} DELETED - Inspection deleted
 */
export enum StatusInspection {
  NOT_STARTED = 'NOT_STARTED',
  SENT = 'SENT',
  INITIATED = 'INITIATED',
  IN_PROGRESS = 'IN_PROGRESS',
  IN_REVIEW = 'IN_REVIEW',
  RE_DO = 'RE_DO',
  EXPIRED = 'EXPIRED',
  COMPLETED = 'COMPLETED',
  CANCELED = 'CANCELED',
  REQUIRES_ATTENTION = 'REQUIRES_ATTENTION',
  ON_HOLD = 'ON_HOLD',
  DELETED = 'DELETED',
}

/**
 * Enum for inspection estimate request status.
 * @enum {string}
 * @property {string} ESTIMATE_REQUESTED - Estimate was requested.
 * @property {string} ESTIMATE_COMPLETED - Estimate was completed.
 */
export enum InspectionEstimateRequestStatus {
  ESTIMATE_REQUESTED = 'ESTIMATE_REQUESTED',
  ESTIMATE_COMPLETED = 'ESTIMATE_COMPLETED',
}

/**
 * Kind of a post-completion service request round. Also determines the Inspector Panel mode:
 * an ESTIMATE round forbids adding damages and keeps non-selected damages read-only, while an
 * UPGRADE round is a normal processing pass in which the inspector may add damages.
 * @enum {string}
 * @property {string} ESTIMATE - Estimate requested for a selected subset of damages.
 * @property {string} UPGRADE - Additional services requested for a previously service-less inspection.
 */
export enum InspectionServiceRequestKind {
  ESTIMATE = 'ESTIMATE',
  UPGRADE = 'UPGRADE',
}

/**
 * Archived scope of the inspection LIST surfaces. Not a lifecycle status: archiving is a
 * list-visibility flag (`archived_at`), and this enum only selects which slice a list returns.
 * Absent/ACTIVE = active list (archived excluded), ARCHIVED = archived-only, ALL = both.
 */
export enum InspectionArchivedScope {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
  ALL = 'ALL',
}

/**
 * Lifecycle of a single service request round.
 * @enum {string}
 * @property {string} REQUESTED - Round is open; the Inspector Panel work item is in progress.
 * @property {string} COMPLETED - Round was completed by the inspector.
 */
export enum InspectionServiceRequestStatus {
  REQUESTED = 'REQUESTED',
  COMPLETED = 'COMPLETED',
}

/**
 * Services a client may request on a completed inspection that ran without any services enabled.
 * @enum {string}
 * @property {string} AI - AI damage detection only.
 * @property {string} AI_WITH_ESTIMATES - AI damage detection plus estimates.
 */
export enum InspectionUpgradeTarget {
  AI = 'AI',
  AI_WITH_ESTIMATES = 'AI_WITH_ESTIMATES',
}

/**
 * Enum for status step
 * @enum {string}
 * @property {string} INACTIVE - Inactive status
 * @property {string} ACTIVE - Active status
 * @property {string} REDO - Redo status
 * @property {string} SKIPPED - Skipped status
 * @property {string} COMPLETED - Completed status
 */
export enum StatusStep {
  INACTIVE = 'INACTIVE',
  ACTIVE = 'ACTIVE',
  REDO = 'REDO',
  SKIPPED = 'SKIPPED',
  COMPLETED = 'COMPLETED',
}

/**
 * The category type mask(step type)
 * @enum CategoryTypeMask
 * @param {string} EXTERIOR - Exterior
 * @param {string} INTERIOR - Interior
 * @param {string} WHEEL - Wheel
 * @param {string} MECHANICAL - Mechanical
 */
export enum CategoryTypeMask {
  EXTERIOR = 'EXTERIOR',
  INTERIOR = 'INTERIOR',
  WHEEL = 'WHEEL',
  MECHANICAL = 'MECHANICAL',
}

/**
 * Enum for the status of uploaded file
 * @enum {string}
 * @property {string} PENDING - The file is pending
 * @property {string} SIZED - The file is sized
 */
export enum StatusFile {
  PENDING = 'PENDING',
  SIZED = 'SIZED',
}

/**
 * Enum for inspection sort fields
 * @enum {string}
 * @property {string} id - Sort by inspection ID
 * @property {string} vinNumber - Sort by vehicle identification number (VIN)
 * @property {string} status - Sort by inspection status
 * @property {string} createdAt - Sort by creation date
 * @property {string} completedAt - Sort by completion date
 * @property {string} estimate - Sort by estimate amount
 * @property {string} cr - Sort by customer rating
 */
export enum InspectionSortField {
  id = 'id',
  vinNumber = 'vinNumber',
  status = 'status',
  createdAt = 'createdAt',
  completedAt = 'completedAt',
  estimate = 'estimate',
  cr = 'cr',
}

/**
 * Enum for period types
 * @enum {string}
 * @property {string} DAY - Day period
 * @property {string} WEEK - Week period
 * @property {string} MONTH - Month period
 * @property {string} YEAR - Year period
 * @property {string} ALL - All periods
 */
export enum PeriodType {
  DAY = 'd',
  WEEK = 'w',
  MONTH = 'm',
  YEAR = 'y',
  ALL = 'all',
}

/**
 * Enum for inspection prefix
 * @enum {string}
 * @property {string} INSPECTION - Prefix for inspection entities
 */
export enum InspectionPrefix {
  INSPECTION = 'ins',
}

/**
 * Type representing the reasons why a link may be unavailable.
 * @typedef {'NOT_READY' | 'LOCKED' | 'EXPIRED' | 'DISABLED_BY_POLICY' | 'PERMISSION_DENIED' | 'UNKNOWN'} LinkReasonUnavailable
 * @property {string} NOT_READY - The link is not ready yet.
 * @property {string} LOCKED - The link is locked.
 * @property {string} EXPIRED - The link has expired.
 * @property {string} DISABLED_BY_POLICY - The link is disabled by policy.
 * @property {string} PERMISSION_DENIED - Permission to access the link is denied.
 * @property {string} UNKNOWN - The reason is unknown.
 */
export type LinkReasonUnavailable =
  | 'NOT_READY'
  | 'LOCKED'
  | 'EXPIRED'
  | 'DISABLED_BY_POLICY'
  | 'PERMISSION_DENIED'
  | 'UNKNOWN';

/**
 * Type representing the audience for a link.
 * @typedef {'internal' | 'partner' | 'customer' | 'public'} LinkAudience
 * @property {string} internal - Internal users
 * @property {string} partner - Partner users
 * @property {string} customer - Customer users
 * @property {string} public - Public users
 */
export type LinkAudience = 'internal' | 'partner' | 'customer' | 'public';

/**
 * Enum for tire types.
 * @enum {string}
 * @property {string} PASSENGER - Passenger tire (P)
 * @property {string} LIGHT_TRUCK - Light truck tire (LT)
 * @property {string} SPECIAL_TRAILER - Special trailer tire (ST)
 * @property {string} TEMPORARY - Temporary tire (T)
 */
export enum TireType {
  PASSENGER = 'P',
  LIGHT_TRUCK = 'LT',
  SPECIAL_TRAILER = 'ST',
  TEMPORARY = 'T',
}

/**
 * Enum for inspection duplication modes.
 * @enum {string}
 * @property {string} AUDIT - Duplicate the inspection in audit mode.
 * @property {string} TRAINING - Duplicate the inspection in training mode.
 */
export enum InspectionDuplicateMode {
  AUDIT = 'AUDIT',
  TRAINING = 'TRAINING',
}

/**
 * Enum for reasons why an inspection action is unavailable.
 * @enum {string}
 * @property {string} NOT_COMPLETED - Inspection is not completed.
 * @property {string} UNSUPPORTED_COMPANY_TYPE - Company type is not supported.
 * @property {string} NO_DAMAGES - No damages were found.
 * @property {string} EST_SERVICE_ALREADY_ENABLED - Estimate service is already enabled.
 * @property {string} AIWH_SERVICE_ALREADY_ENABLED - AIwH service is already enabled.
 * @property {string} ESTIMATE_ALREADY_REQUESTED - An estimate round is already open.
 * @property {string} ESTIMATE_ALREADY_COMPLETED - Estimate has already been completed.
 * @property {string} UPGRADE_ALREADY_REQUESTED - An upgrade round is already open.
 * @property {string} SERVICES_ALREADY_ENABLED - Inspection already runs the services being requested.
 * @property {string} NOT_UPGRADEABLE_FLOW - Inspection flow type does not support a service upgrade.
 * @property {string} UPGRADE_REQUIRED - A service upgrade is available and must happen first; the
 * estimate action is hidden while "Request Upgraded Services" is the offered path.
 */
export enum InspectionActionUnavailableReason {
  NOT_COMPLETED = 'NOT_COMPLETED',
  UNSUPPORTED_COMPANY_TYPE = 'UNSUPPORTED_COMPANY_TYPE',
  NO_DAMAGES = 'NO_DAMAGES',
  EST_SERVICE_ALREADY_ENABLED = 'EST_SERVICE_ALREADY_ENABLED',
  AIWH_SERVICE_ALREADY_ENABLED = 'AIWH_SERVICE_ALREADY_ENABLED',
  ESTIMATE_ALREADY_REQUESTED = 'ESTIMATE_ALREADY_REQUESTED',
  ESTIMATE_ALREADY_COMPLETED = 'ESTIMATE_ALREADY_COMPLETED',
  UPGRADE_ALREADY_REQUESTED = 'UPGRADE_ALREADY_REQUESTED',
  SERVICES_ALREADY_ENABLED = 'SERVICES_ALREADY_ENABLED',
  NOT_UPGRADEABLE_FLOW = 'NOT_UPGRADEABLE_FLOW',
  UPGRADE_REQUIRED = 'UPGRADE_REQUIRED',
}

/**
 * Enum describing which lifecycle family owns an inspection, as exposed by the Integration Execution API.
 * @enum {string}
 * @property {string} STANDALONE_INSPECTION - Dealer/Leasing standalone inspection (no rental contract).
 * @property {string} RENTAL_CONTRACT_INSPECTION - Rental contract-owned inspection (pickup/drop-off/employee).
 */
export enum InspectionLifecycleType {
  STANDALONE_INSPECTION = 'STANDALONE_INSPECTION',
  RENTAL_CONTRACT_INSPECTION = 'RENTAL_CONTRACT_INSPECTION',
}

/**
 * Machine-readable reasons why an inspection or rental contract cannot currently be executed
 * through the Integration Execution API.
 * @enum {string}
 * @property {string} INSPECTION_NOT_SUBMITTABLE - Inspection status does not allow submission.
 * @property {string} INSPECTION_ALREADY_COMPLETED - Inspection has already been passed (COMPLETED or IN_REVIEW).
 * @property {string} INSPECTION_CANCELED - Inspection was canceled.
 * @property {string} INSPECTION_DELETED - Inspection was deleted.
 * @property {string} INSPECTION_ON_HOLD - Inspection is on hold.
 * @property {string} INSPECTION_EXPIRED - Inspection link expired before the client started it.
 * @property {string} CONTRACT_NOT_READY - Contract is still SCHEDULED; execution has not started yet.
 * @property {string} CONTRACT_NOT_ACTIVE - Contract is canceled or deleted.
 * @property {string} CONTRACT_ALREADY_COMPLETED - Contract lifecycle is finished.
 * @property {string} CONTRACT_ESTIMATE_REQUESTED - Contract is awaiting a requested estimate.
 * @property {string} AWAITING_VIDEO_FRAMES - Inspection was confirmed but 360-video frames are not sliced yet.
 */
export enum ExecutionBlockingReason {
  INSPECTION_NOT_SUBMITTABLE = 'INSPECTION_NOT_SUBMITTABLE',
  INSPECTION_ALREADY_COMPLETED = 'INSPECTION_ALREADY_COMPLETED',
  INSPECTION_CANCELED = 'INSPECTION_CANCELED',
  INSPECTION_DELETED = 'INSPECTION_DELETED',
  INSPECTION_ON_HOLD = 'INSPECTION_ON_HOLD',
  INSPECTION_EXPIRED = 'INSPECTION_EXPIRED',
  CONTRACT_NOT_READY = 'CONTRACT_NOT_READY',
  CONTRACT_NOT_ACTIVE = 'CONTRACT_NOT_ACTIVE',
  CONTRACT_ALREADY_COMPLETED = 'CONTRACT_ALREADY_COMPLETED',
  CONTRACT_ESTIMATE_REQUESTED = 'CONTRACT_ESTIMATE_REQUESTED',
  AWAITING_VIDEO_FRAMES = 'AWAITING_VIDEO_FRAMES',
}
